/**
 * Pure helpers for keeping the reaction diagram in step with the stoichiometry table. The React
 * state (when to rebuild, the banner, in-flight builds) lives in useSyncReactionComponentsFromTable.
 *
 * The diagram is a saved file. The table's `syncedComponents` metadata records which reactants and
 * products, in order, that file was last built from, and the version of each molecule. Comparing
 * that record with what the table holds now tells whether the diagram is stale.
 */
import reactUpdate from 'immutability-helper';

import {ReactionComponentInput} from 'chemistry/ChemistryDAO';
import {Status} from 'constants/StatusConstants';
import {
  ChemistryReactionRole,
  CHEMISTRY_REACTION_ROLE_DISPLAY_NAME_TO_ENUM,
} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {
  getColumnIndices,
  getMoleculeIdFromEvaluatedCell,
  getMoleculeVersionIdFromEvaluatedCell,
  getRoleFromEvaluatedCell,
} from 'notebook/integrations/chemistry/reaction-scheme/StoichiometryTableHelpers';
import {
  StoichiometryStructuredTable,
  SyncedReactionComponent,
  SyncedReactionComponents,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {EvaluatedCell, EvaluatedTable} from 'notebook/tables/excess/EvaluationConstants';
import {hasErrorType, LoadingError} from 'notebook/tables/excess/Util';
import {CustomResolutionQueryType} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';

/** The diagram shows only reactants and products; other roles are left out. */
export type DiagramRole = ChemistryReactionRole.REACTANT | ChemistryReactionRole.PRODUCT;
const DIAGRAM_ROLES: Array<DiagramRole> = [ChemistryReactionRole.REACTANT, ChemistryReactionRole.PRODUCT];

/** Reactants and products in table order, without the legacy plain-string form. */
export type ReactionComponents = Record<DiagramRole, Array<SyncedReactionComponent>>;

function isDiagramRole(role: ChemistryReactionRole | null | undefined): role is DiagramRole {
  return role === ChemistryReactionRole.REACTANT || role === ChemistryReactionRole.PRODUCT;
}

function mapRoles<T>(fn: (role: DiagramRole) => T): Record<DiagramRole, T> {
  return {
    [ChemistryReactionRole.REACTANT]: fn(ChemistryReactionRole.REACTANT),
    [ChemistryReactionRole.PRODUCT]: fn(ChemistryReactionRole.PRODUCT),
  };
}

/** Reads the diagram's recorded components. Older tables store plain molecule ID strings. */
export function readDiagramComponents(
  syncedComponents: SyncedReactionComponents | undefined
): ReactionComponents | undefined {
  if (syncedComponents == null) {
    return undefined;
  }
  return mapRoles((role) =>
    syncedComponents[role].map((c) => (typeof c === 'string' ? {type: 'molecule' as const, id: c} : c))
  );
}

/** A reactant or product row of the evaluated table. */
export interface ReactionRow {
  rowIndex: number;
  rowId: string;
  role: DiagramRole;
  /** Null while the row has neither a molecule nor an unrecognized structure's SMILES. */
  component: SyncedReactionComponent | null;
}

/**
 * Reads the reactant and product rows from the evaluated table. A molecule's version is left unknown
 * while its Molecule Version cell is still resolving: until then the cell shows its saved version,
 * which may not be the molecule's current one.
 */
export function readReactionRows(
  columns: StoichiometryStructuredTable['columns'],
  tableRows: StoichiometryStructuredTable['rows'],
  computedTable: EvaluatedTable
): Array<ReactionRow> {
  const {compoundColumnIndex, roleColumnIndex, versionColumnIndex} = getColumnIndices(columns);
  return computedTable.flatMap((evaluatedRow, rowIndex): Array<ReactionRow> => {
    const role = getRoleFromEvaluatedCell(evaluatedRow[roleColumnIndex]);
    const rowId = tableRows[rowIndex]?.rowId;
    if (!isDiagramRole(role) || rowId == null) {
      return [];
    }
    const moleculeId = getMoleculeIdFromEvaluatedCell(evaluatedRow[compoundColumnIndex]);
    if (moleculeId != null) {
      const versionCell = versionColumnIndex === -1 ? undefined : evaluatedRow[versionColumnIndex];
      const versionId =
        versionCell != null && !hasErrorType(versionCell.error, LoadingError.type)
          ? getMoleculeVersionIdFromEvaluatedCell(versionCell)
          : null;
      const component: SyncedReactionComponent = {
        type: 'molecule',
        id: moleculeId,
        ...(versionId != null ? {versionId} : {}),
      };
      return [{rowIndex, rowId, role, component}];
    }
    const smiles = tableRows[rowIndex]?.metadata?.canonicalSmiles;
    return [{rowIndex, rowId, role, component: smiles ? {type: 'smiles', smiles} : null}];
  });
}

export function toReactionComponents(rows: Array<ReactionRow>): ReactionComponents {
  const components: ReactionComponents = mapRoles(() => []);
  for (const {role, component} of rows) {
    if (component != null) {
      components[role].push(component);
    }
  }
  return components;
}

/** The same molecule or structure. Versions are compared only when both sides know one. */
function isSameComponent(a: SyncedReactionComponent, b: SyncedReactionComponent): boolean {
  if (a.type === 'smiles' || b.type === 'smiles') {
    return a.type === 'smiles' && b.type === 'smiles' && a.smiles === b.smiles;
  }
  return a.id === b.id && (a.versionId == null || b.versionId == null || a.versionId === b.versionId);
}

/**
 * - `untracked`: the table predates `syncedComponents`, so there's no record to compare against.
 * - `stale`: the table's reactants or products, their order, or a known version differ from the
 *   diagram.
 * - `missingVersions`: not stale, but the diagram predates version tracking and the table now knows
 *   versions it never recorded. A diagram can also be missing versions while stale; it's reported as
 *   `stale`, because the rebuild records the versions too.
 * - `inSync`: nothing to do.
 */
export type DiagramStatus = 'untracked' | 'stale' | 'missingVersions' | 'inSync';

export function getDiagramStatus(
  table: ReactionComponents,
  diagram: ReactionComponents | undefined
): DiagramStatus {
  if (diagram == null) {
    return 'untracked';
  }
  const matches = DIAGRAM_ROLES.every(
    (role) =>
      table[role].length === diagram[role].length &&
      table[role].every((component, index) => isSameComponent(component, diagram[role][index]))
  );
  if (!matches) {
    return 'stale';
  }
  const diagramIsMissingVersions = DIAGRAM_ROLES.some((role) =>
    table[role].some((component, index) => {
      const recorded = diagram[role][index];
      return (
        component.type === 'molecule' &&
        component.versionId != null &&
        recorded.type === 'molecule' &&
        recorded.versionId == null
      );
    })
  );
  return diagramIsMissingVersions ? 'missingVersions' : 'inSync';
}

/**
 * Components to record for a diagram that already matches the table. Keeps a version the diagram
 * recorded where the table's is still unknown.
 */
export function withRecordedVersions(
  table: ReactionComponents,
  diagram: ReactionComponents
): ReactionComponents {
  return mapRoles((role) =>
    table[role].map((component, index) => {
      const recorded = diagram[role][index];
      const keepRecordedVersion =
        component.type === 'molecule' &&
        component.versionId == null &&
        recorded?.type === 'molecule' &&
        recorded.versionId != null;
      return keepRecordedVersion ? {...component, versionId: recorded.versionId} : component;
    })
  );
}

/** Molecule ID -> version ID, for molecules whose version is known. */
export function knownVersionIds(components: ReactionComponents): Map<string, string> {
  const versionIds = new Map<string, string>();
  for (const role of DIAGRAM_ROLES) {
    for (const component of components[role]) {
      if (component.type === 'molecule' && component.versionId != null) {
        versionIds.set(component.id, component.versionId);
      }
    }
  }
  return versionIds;
}

// A bioentity id or SMILES string identifying a cell's molecule, deliberately excluding the display
// name (names hydrate asynchronously on a cold load, so keying on them would look like an edit).
function savedMoleculeIdentityOf(cell: Cell | undefined): string {
  const typedValue = cell?.typedValue;
  if (typedValue?.type === BaseTypedValueType.BIOENTITY && typedValue.bioentitySummary?.id != null) {
    return `id:${typedValue.bioentitySummary.id}`;
  }
  if (typedValue?.type === BaseTypedValueType.STRING && typedValue.rawValue) {
    return `text:${typedValue.rawValue}`;
  }
  return 'empty';
}

// The saved Role cell holds the role as a typed string, or only as text.
function savedRoleOf(cell: Cell | undefined): ChemistryReactionRole | null {
  const roleText =
    cell?.typedValue?.type === BaseTypedValueType.STRING ? cell.typedValue.rawValue : cell?.text;
  return roleText != null ? (CHEMISTRY_REACTION_ROLE_DISPLAY_NAME_TO_ENUM[roleText] ?? null) : null;
}

/**
 * Role, lot and compound (ID or SMILES text) of each reactant and product row, read from the saved
 * cells. These change only when someone edits the table. The evaluated table can't be used for this:
 * after the Entry opens, its molecules resolve, a lot fills in its compound, and names load, all
 * asynchronously, and any of that would look like an edit.
 */
export function readSavedReactionIdentity(
  columns: StoichiometryStructuredTable['columns'],
  rows: StoichiometryStructuredTable['rows']
): Array<string> {
  const {compoundColumnIndex, roleColumnIndex, lotColumnIndex} = getColumnIndices(columns);
  return rows.flatMap((row) => {
    const role = savedRoleOf(row.cells[roleColumnIndex]);
    if (!isDiagramRole(role)) {
      return [];
    }
    const lot = lotColumnIndex === -1 ? 'empty' : savedMoleculeIdentityOf(row.cells[lotColumnIndex]);
    const compound = savedMoleculeIdentityOf(row.cells[compoundColumnIndex]);
    return [`${role}|lot:${lot}|compound:${compound}`];
  });
}

function hasPendingLotResolution(cell: EvaluatedCell | undefined): boolean {
  return (
    cell?.resolutions?.some(
      (resolution) =>
        resolution.query.type === ResolutionQueryType.CUSTOM &&
        resolution.query.customResolutionQueryType ===
          CustomResolutionQueryType.STOICHIOMETRY_TABLE_GET_LOT_PROPERTIES &&
        resolution.resolvedValue == null &&
        resolution.error == null
    ) ?? false
  );
}

/** True once every reaction input cell has finished evaluating. */
export function isTableEvaluated(
  structuredTable: StoichiometryStructuredTable,
  computedTable: EvaluatedTable
): boolean {
  const {compoundColumnIndex, roleColumnIndex, lotColumnIndex, versionColumnIndex} = getColumnIndices(
    structuredTable.columns
  );
  const columnIndices = [compoundColumnIndex, roleColumnIndex, lotColumnIndex, versionColumnIndex].filter(
    (columnIndex) => columnIndex !== -1
  );
  return (
    computedTable.length === structuredTable.rows.length &&
    !computedTable.some((evaluatedRow) =>
      columnIndices.some((columnIndex) => {
        const cell = evaluatedRow[columnIndex];
        const value = cell?.value;
        return (
          (value?.type === BaseTypedValueType.COMPUTING && value.computedState.status !== Status.FAILED) ||
          hasErrorType(cell?.error, LoadingError.type) ||
          hasPendingLotResolution(cell)
        );
      })
    )
  );
}

export interface DiagramBuildRequest {
  reactants: Array<ReactionComponentInput>;
  products: Array<ReactionComponentInput>;
  /** What to record in `syncedComponents` once the build is saved. */
  components: ReactionComponents;
  /** Rows whose drawn layout was skipped as stale, to clear once the build is saved. */
  staleDrawingRowIds: Array<string>;
}

/**
 * The build-from-components request for the table's current reactants and products. A row's drawn
 * layout (`drawnMolfile`) keeps the user's 2D orientation, unless its molecule has a different known
 * version than the diagram recorded: then the drawing shows an old structure, so the molecule is
 * resolved fresh and the drawing is cleared.
 */
export function makeDiagramBuildRequest(
  tableRows: StoichiometryStructuredTable['rows'],
  rows: Array<ReactionRow>,
  diagram: ReactionComponents | undefined
): DiagramBuildRequest {
  const request: DiagramBuildRequest = {
    reactants: [],
    products: [],
    components: toReactionComponents(rows),
    staleDrawingRowIds: [],
  };
  for (const {rowIndex, rowId, role, component} of rows) {
    const drawnMolfile = tableRows[rowIndex]?.metadata?.drawnMolfile;
    const recorded =
      component?.type === 'molecule'
        ? diagram?.[role].find((c) => c.type === 'molecule' && c.id === component.id)
        : undefined;
    const isDrawingStale =
      component?.type === 'molecule' &&
      component.versionId != null &&
      recorded?.type === 'molecule' &&
      recorded.versionId != null &&
      recorded.versionId !== component.versionId;
    if (drawnMolfile && isDrawingStale) {
      request.staleDrawingRowIds.push(rowId);
    }
    let input: ReactionComponentInput | null = null;
    if (drawnMolfile && !isDrawingStale) {
      input = {type: 'molfile', molfile: drawnMolfile};
    } else if (component?.type === 'molecule') {
      input = {type: 'molecule', moleculeId: component.id};
    } else if (component?.type === 'smiles') {
      input = {type: 'smiles', smiles: component.smiles};
    }
    if (input != null) {
      (role === ChemistryReactionRole.REACTANT ? request.reactants : request.products).push(input);
    }
  }
  return request;
}

/** The table to save with a newly built diagram: its recorded components, minus stale drawings. */
export function tableForBuiltDiagram(
  structuredTable: StoichiometryStructuredTable,
  {components, staleDrawingRowIds}: Pick<DiagramBuildRequest, 'components' | 'staleDrawingRowIds'>
): StoichiometryStructuredTable {
  const staleRowIds = new Set(staleDrawingRowIds);
  return reactUpdate(structuredTable, {
    metadata: {$merge: {syncedComponents: components}},
    ...(staleRowIds.size > 0
      ? {
          rows: {
            $set: structuredTable.rows.map((row) =>
              staleRowIds.has(row.rowId)
                ? reactUpdate(row, {metadata: {$unset: ['drawnMolfile', 'drawnMolfileMoleculeId']}})
                : row
            ),
          },
        }
      : {}),
  });
}
